import * as THREE from 'three';
import { HEIGHT_SCALE, Terrain, type GameMap } from '../../world/maps/mapTypes';
import { applyFow } from './fowShader';
import { terrainTextures } from './textures';

/** Float height sampler for rendering (bilinear over vertex heights). */
export class HeightSampler {
  constructor(private map: GameMap) {}
  at(x: number, z: number): number {
    const m = this.map;
    const vs = m.size + 1;
    const cx = Math.min(m.size - 0.001, Math.max(0, x));
    const cz = Math.min(m.size - 0.001, Math.max(0, z));
    const ix = Math.floor(cx);
    const iz = Math.floor(cz);
    const fx = cx - ix;
    const fz = cz - iz;
    const h = m.heights;
    const a = h[iz * vs + ix];
    const b = h[iz * vs + ix + 1];
    const c = h[(iz + 1) * vs + ix];
    const d = h[(iz + 1) * vs + ix + 1];
    return ((a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz) / HEIGHT_SCALE;
  }
}

/**
 * Terrain mesh: per-vertex splat weights blend six procedural texture layers
 * (grass, dirt, rock, sand, snow, forest floor), with two sampling scales to
 * suppress visible tiling. Receives shadows and fog of war.
 */
export function buildTerrain(map: GameMap, detail: number): THREE.Mesh {
  const size = map.size;
  const step = detail >= 2 ? 1 : 1; // full-resolution grid; LOD via shader detail
  const vs = size + 1;
  const pos = new Float32Array(vs * vs * 3);
  for (let z = 0; z < vs; z++)
    for (let x = 0; x < vs; x++) {
      const i = z * vs + x;
      pos[i * 3] = x;
      pos[i * 3 + 1] = map.heights[i] / HEIGHT_SCALE;
      pos[i * 3 + 2] = z;
    }
  // Ground-type weights live in two textures with one texel per tile, sampled with bilinear filtering
  // (and a slight noise warp) in the fragment shader. Per-vertex weights were interpolated across each
  // tile's two triangles, which drew straight seams along tile edges and diagonals.
  const splatA = new Uint8Array(size * size * 4);
  const splatB = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const t = map.terrain[i];
    const layer = t === Terrain.Grass ? 0 : t === Terrain.Dirt ? 1 : t === Terrain.Rock ? 2 : t === Terrain.Snow ? 4 : t === Terrain.Forest ? 5 : t === Terrain.Road ? 6 : 3;
    if (layer < 4) splatA[i * 4 + layer] = 255;
    else splatB[i * 4 + layer - 4] = 255;
  }
  const splatTex = (data: Uint8Array) => {
    const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
    t.magFilter = THREE.LinearFilter;
    t.minFilter = THREE.LinearFilter;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.needsUpdate = true;
    return t;
  };
  const idx: number[] = [];
  for (let z = 0; z < size; z += step)
    for (let x = 0; x < size; x += step) {
      const a = z * vs + x;
      const b = a + 1;
      const c = a + vs;
      const d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  geo.computeBoundingSphere();

  const tex = terrainTextures(detail >= 2 ? 512 : 256);
  const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.96, metalness: 0 });
  applyFow(mat, (shader) => {
    shader.uniforms.tGrass = { value: tex.grass };
    shader.uniforms.tDirt = { value: tex.dirt };
    shader.uniforms.tRock = { value: tex.rock };
    shader.uniforms.tSand = { value: tex.sand };
    shader.uniforms.tSnow = { value: tex.snow };
    shader.uniforms.tForest = { value: tex.forest };
    shader.uniforms.tRoad = { value: tex.road };
    shader.uniforms.tSplatA = { value: splatTex(splatA) };
    shader.uniforms.tSplatB = { value: splatTex(splatB) };
    shader.uniforms.uMapSize = { value: size };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vTerr;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvTerr = position;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        uniform sampler2D tGrass, tDirt, tRock, tSand, tSnow, tForest, tRoad, tSplatA, tSplatB;
        uniform float uMapSize;
        varying vec3 vTerr;
        vec3 layer(sampler2D t, vec2 uv) {
          return mix(texture2D(t, uv).rgb, texture2D(t, uv * 0.23 + 0.31).rgb, 0.4);
        }`,
      )
      .replace(
        '#include <map_fragment>',
        `{
          vec2 uv = vTerr.xz * 0.21;
          // Splat lookup: texel centres sit on tile centres; a small noise warp keeps the boundaries
          // between ground types from following the tile grid.
          vec2 warp = (texture2D(tSand, vTerr.xz * 0.037).rg - 0.5) * 0.9;
          vec2 suv = (vTerr.xz + warp) / uMapSize;
          vec4 a = texture2D(tSplatA, suv);
          vec4 b = texture2D(tSplatB, suv);
          // Sharpen transitions using a noise-modulated weight.
          float nz = texture2D(tDirt, vTerr.xz * 0.05).r;
          a = pow(a + vec4(0.0001), vec4(1.6 + nz));
          b = pow(b + vec4(0.0001), vec4(1.6 + nz));
          float sum = a.x + a.y + a.z + a.w + b.x + b.y + b.z;
          vec3 c = (layer(tGrass, uv) * a.x + layer(tDirt, uv) * a.y + layer(tRock, uv * 0.7) * a.z
                  + layer(tSand, uv) * a.w + layer(tSnow, uv) * b.x + layer(tForest, uv) * b.y + layer(tRoad, uv * 1.3) * b.z) / sum;
          // Underwater darkening for shallows/seabed.
          float under = clamp(-vTerr.y * 1.2, 0.0, 1.0);
          c = mix(c, c * vec3(0.55, 0.65, 0.6), under);
          // Large-scale tonal variation.
          float macro = texture2D(tGrass, vTerr.xz * 0.013).g;
          c *= 0.85 + macro * 0.35;
          diffuseColor.rgb *= c;
        }`,
      );
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.name = 'terrain';
  return mesh;
}

/** Heightmap as a float texture (for water depth shading). */
export function heightTexture(map: GameMap): THREE.DataTexture {
  const vs = map.size + 1;
  const data = new Uint8Array(vs * vs * 4);
  for (let i = 0; i < vs * vs; i++) {
    // Encode height in [-6, 10] world units into 0..255.
    const h = map.heights[i] / HEIGHT_SCALE;
    const v = Math.max(0, Math.min(255, Math.round(((h + 6) / 16) * 255)));
    data[i * 4] = v;
    data[i * 4 + 1] = v;
    data[i * 4 + 2] = v;
    data[i * 4 + 3] = 255;
  }
  const t = new THREE.DataTexture(data, vs, vs, THREE.RGBAFormat);
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}
